import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import Axios from 'axios';
import pmsConstants from '../pmsConstants.js';
import PmsUrlConfig from '../urlConfig.js';
import { saveUser, savePracticeDetails } from '../utils/storageUtils.js';
import { safeSetInterval, clearAllIntervals } from '../utils/intervalManager.js';
import { fetchUserAppointments } from '../components/appointment/appointmentFunctions.js';
import { populateServiceTypes } from '../components/common.js';
import { authenticateMatrixClient } from '../services/matrixAuthService.js';
import { LOGIN_ERROR_MESSAGES, APPOINTMENT_CHECK_INTERVAL } from '../constants/loginConstants.js';
import { toast } from 'react-toastify';
import { fetchSystemConstantsAction } from '../actions/systemConstantsActions.js';
import 'react-toastify/dist/ReactToastify.css';

export const useAuth = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loginDetails = useSelector(state => state.loginDetails);
    
    const [passwordType, setPasswordType] = useState("password");

    const error = loginDetails?.loginError || '';

    const togglePassword = () => {
        setPasswordType(prev => prev === "password" ? "text" : "password");
    };

    const handleLogin = async (values, { setSubmitting }) => {
        try {
            // Clear any existing intervals to prevent duplicates
            clearAllIntervals();
            
            dispatch({ type: 'login' });

            const response = await Axios.post(PmsUrlConfig.BOS_LOGIN_SERVICE_URL, {
                username: values.username,
                password: values.password,
            });

            if (response.status === 200) {
                const loggedInUser = await processLoginResponse(response.data, dispatch);
                
                if (loggedInUser) {
                    await handlePostLogin(loggedInUser, dispatch);
                    navigate(`${PmsUrlConfig.WELCOME_URL}`);
                }
            } else if (response.status === 400) {
                const errorMessage = response.data.error_code.description || LOGIN_ERROR_MESSAGES.INVALID_CREDENTIALS;
                
                toast.error(errorMessage, {
                    position: "bottom-center",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
                
                dispatch({
                    type: 'error',
                    payload: errorMessage
                });
                console.log('Login error:', errorMessage);
            } else {
                dispatch({
                    type: 'error',
                    payload: LOGIN_ERROR_MESSAGES.INVALID_CREDENTIALS
                });
            }
        } catch (error) {
            handleLoginError(error, dispatch, setSubmitting);
        }
    };

    const processLoginResponse = async (responseData, dispatch) => {
        let userType = pmsConstants.USER_TYPE_PATIENT;
        let loggedInUser = responseData.LoggedInUser;
        let token = "";

        if (loggedInUser !== null) {
            userType = loggedInUser.user_type;
            token = loggedInUser.jwt_token;
        }

        if (userType !== pmsConstants.USER_TYPE_PATIENT) {
            dispatch({
                type: 'practiceLoginSuccess',
                payload: responseData.Practice,
            });
            savePracticeDetails(responseData.Practice);

            // Note: Intervals are now managed by the component hooks to prevent duplicates
            // Removed: safeSetInterval(checkForNewAppointments, APPOINTMENT_CHECK_INTERVAL, 'Practice Appointments Check', serviceIds, dispatch, token);
        }

        if (userType === pmsConstants.USER_TYPE_PATIENT) {
            fetchUserAppointments(loggedInUser._id, 0, dispatch, token);
            safeSetInterval(fetchUserAppointments, APPOINTMENT_CHECK_INTERVAL, 'Patient Appointments Check', loggedInUser._id, 0, dispatch, token);
        }

        dispatch({
            type: 'loginSuccess',
            payload: loggedInUser
        });

        return loggedInUser;
    };

    const handlePostLogin = async (loggedInUser, dispatch) => {
        console.log('=== POST LOGIN ANALYSIS ===');
        console.log('User Type:', loggedInUser.user_type);
        console.log('User ID:', loggedInUser._id);
        console.log('Full user object keys:', Object.keys(loggedInUser));
        console.log('Synapse credentials check:');
        console.log('  - synapse_user_id:', loggedInUser.synapse_user_id);
        console.log('  - synapse_password:', loggedInUser.password ? '[REDACTED]' : 'MISSING');
        console.log('=== END POST LOGIN ANALYSIS ===');
        
        const jwt_token = loggedInUser.jwt_token;
        
        // Initialize Matrix authentication in background
        authenticateMatrixClient(loggedInUser, dispatch)
            .then(() => {
                console.log('✅ Matrix authentication completed successfully');
            })
            .catch((error) => {
                console.error('❌ Matrix authentication failed:', error);
                dispatch({
                    type: 'matrixAuthError',
                    payload: LOGIN_ERROR_MESSAGES.MATRIX_AUTH_WARNING
                });
            });

        saveUser(loggedInUser);
        dispatch({
            type: 'setJwtToken',
            payload: jwt_token
        });
        populateServiceTypes(dispatch, jwt_token);
        
        // Fetch system constants after successful login
        fetchSystemConstantsAction(jwt_token)(dispatch)
            .then(() => {
                console.log('System constants fetched successfully');
            })
            .catch((error) => {
                console.error('Failed to fetch system constants:', error);
            });
    };

    const handleLoginError = (error, dispatch, setSubmitting) => {
        console.log("-----------------", error);
        setSubmitting(false);
        
        if (error.response?.data?.error_code) {
            const errorMessage = error.response.data.error_code.description;
            toast.error(errorMessage, {
                position: "bottom-center",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
            dispatch({
                type: 'error',
                payload: errorMessage
            });
        } else {
            toast.error(LOGIN_ERROR_MESSAGES.SERVER_ERROR, {
                position: "bottom-center",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
            dispatch({
                type: 'error',
                payload: LOGIN_ERROR_MESSAGES.SERVER_ERROR
            });
        }
    };

    return {
        error,
        passwordType,
        togglePassword,
        handleLogin
    };
};
